/**
 * Preparation de l'enonce d'un exercice.
 *
 * L'enonce est stocke en base comme une simple chaine. Il peut donc contenir
 * soit du texte brut (cas historique), soit du HTML enrichi necessaire aux
 * exercices de type "reproduire", ou l'eleve doit disposer du rendu cible sous
 * les yeux.
 *
 * Ce module decide lequel des deux et assainit le HTML avant affichage. Il ne
 * reutilise pas `ContentViewer` : la configuration DOMPurify de celui-ci
 * restreint les URL aux liens YouTube, ce qui supprimerait toutes les images.
 */

import DOMPurify from 'dompurify';
import { looksLikeMarkdown, markdownToHtml } from './markdown';

/**
 * Balises autorisees dans un enonce.
 *
 * Volontairement limitees a ce qui sert a decrire une consigne : mise en forme,
 * listes, tableaux, extraits de code et illustrations.
 */
const ALLOWED_TAGS = [
  'p', 'br', 'hr', 'span', 'div',
  'strong', 'b', 'em', 'i', 'u', 's', 'mark', 'small', 'sub', 'sup',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  'blockquote', 'code', 'pre', 'kbd', 'samp',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption',
  'img', 'figure', 'figcaption', 'picture', 'source',
  'a',
];

/**
 * Attributs autorises.
 *
 * `style` est admis pour permettre a l'enseignant de dimensionner une image de
 * reference ; les gestionnaires d'evenements (`onerror`, `onclick`...) sont
 * exclus par DOMPurify.
 */
const ALLOWED_ATTR = [
  'src', 'alt', 'title', 'width', 'height', 'loading', 'srcset', 'sizes',
  'href', 'target', 'rel',
  'class', 'style',
  'colspan', 'rowspan',
];

/**
 * Detecte la presence de balisage.
 *
 * Un enonce en texte brut reste rendu tel quel : les enonces existants ne
 * changent donc pas d'apparence, et un `<` isole dans une phrase ne declenche
 * pas a tort le rendu riche.
 */
export const looksLikeHtml = (statement: string): boolean => /<[a-z][\s\S]*>/i.test(statement);

/** Assainit un enonce HTML avant injection dans le DOM. */
export const sanitizeStatementHtml = (statement: string): string =>
  DOMPurify.sanitize(statement, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // Les liens externes ouverts depuis un enonce ne doivent pas donner acces a
    // la fenetre parente.
    ADD_ATTR: ['target'],
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick'],
  });

export type StatementRendering =
  | { kind: 'empty' }
  | { kind: 'text'; text: string }
  | { kind: 'html'; html: string };

/**
 * Determine comment afficher un enonce.
 *
 * Retourne `empty` lorsqu'il n'y a rien a montrer, pour que l'appelant puisse
 * omettre completement le bloc plutot que d'afficher un cadre vide.
 */
export const prepareStatement = (statement: string | undefined | null): StatementRendering => {
  const trimmed = (statement ?? '').trim();
  if (trimmed === '') return { kind: 'empty' };

  // Markdown d'abord : c'est la seule syntaxe qui permette de citer une balise
  // (`` `<img>` ``) sans que le navigateur l'interprete. Sur ce module qui
  // enseigne le HTML, l'enonce a souvent besoin de montrer du code.
  if (looksLikeMarkdown(trimmed)) {
    const html = sanitizeStatementHtml(markdownToHtml(trimmed));
    return html.trim() === '' ? { kind: 'empty' } : { kind: 'html', html };
  }

  if (!looksLikeHtml(trimmed)) return { kind: 'text', text: trimmed };

  const html = sanitizeStatementHtml(trimmed);
  // L'assainissement peut vider un contenu entierement compose de balises
  // interdites : on evite alors d'afficher un bloc vide.
  return html.trim() === '' ? { kind: 'empty' } : { kind: 'html', html };
};
